import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withHooks, withMethods, withState } from '@ngrx/signals';
import { filter, fromEvent, map } from 'rxjs';
import { ChannelService, ChatClientService } from 'stream-chat-angular';
import { ChatDataStore } from '../../chat/chat-data.store';
import { userState } from '../user/user.feature';

type ChatLatestMessageTimeState = {
  unreadCount: number;
};

export function withUnreadCount() {
  return signalStoreFeature(
    { state: type<userState>() },
    withState<ChatLatestMessageTimeState>({
      unreadCount: 0,
    }),
    withMethods((store, chatDataStore = inject(ChatDataStore), channelService = inject(ChannelService)) => {
      return {
        setMarkActiveAsRead: (isTabActive: boolean) => {
          if (isTabActive) {
            channelService.shouldMarkActiveChannelAsRead = isTabActive;
          }
        },
        updateUnreadCount: () => {
          const incompleteChats = chatDataStore.incompleteChats();

          const channels = channelService.channels.filter((channel) =>
            incompleteChats.some((chat) => chat.channel_id === channel.id)
          );

          const unreadCount = channels.reduce((acc, channel) => {
            return acc + (channel?.countUnread() || 0);
          }, 0);
          patchState(store, { unreadCount });
        },
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        const chatClientService = inject(ChatClientService);

        store.updateUnreadCount();
        chatClientService.events$
          .pipe(
            takeUntilDestroyed(),
            filter((e) => {
              const events = ['message.new', 'message.updated', 'message.deleted', 'message.read', 'channel.truncated'];
              return events.includes(e.eventType);
            }),
            tapResponse({
              next: () => {
                store.updateUnreadCount();
              },
              error: (error) => {
                console.error('Error updating latest message time:', error);
              },
            })
          )
          .subscribe();

        store.setMarkActiveAsRead(isTabActive());

        getPageVisibilityChanges()
          .pipe(takeUntilDestroyed())
          .subscribe((visibilityState) => {
            store.setMarkActiveAsRead(visibilityState === 'visible');
            if (visibilityState === 'visible') {
              store.updateUnreadCount();
            }
          });
      },
    }))
  );
}

const isTabActive = () => {
  return document.visibilityState === 'visible';
};

const getPageVisibilityChanges = () => {
  return fromEvent(document, 'visibilitychange').pipe(map(() => document.visibilityState));
};
